import { useState } from 'react';
import { BaseEditIcon } from '../BaseEditIcon';
import { useAsync } from 'react-use';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { WorkloadClient } from '@internal/workload-api-helper';
import { parseWorkloadObjectValues } from '../utils/workload';
import { getMessage } from '../../../utils/getMessage';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { isOkayResponse, isValueDefined } from '../utils/checks';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { useWorkload } from '../hooks/useWorkload';
import { DisasterRecoveryEditDialog } from './DisasterRecoveryEditDialog';
import { DisasterRecovery } from '../types';

let accountObject: { [key: string]: string | undefined };

interface DisasterRecoveryEditIconProps {
  disasterRecovery: DisasterRecovery;
  onUpdate?: (updatedDisasterRecovery: DisasterRecovery) => void;
}

// Main component
export const DisasterRecoveryEditIcon = ({
  disasterRecovery,
  onUpdate,
}: DisasterRecoveryEditIconProps) => {
  const { entity } = useEntity();
  const { workload: workloadData, loading: workloadLoading } = useWorkload();
  const [dialogOpen, setDialogOpen] = useState(false);
  const identity = useApi(identityApiRef);
  const { value: profile } = useAsync(() => identity.getProfileInfo());
  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(configApi, oauthApi);
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const [loading, setLoading] = useState(false);
  const [updatedDisasterRecovery, setUpdatedDisasterRecovery] =
    useState<DisasterRecovery>(disasterRecovery);

  const hasDisasterRecoveryChanged = (
    original: DisasterRecovery,
    updated: DisasterRecovery,
  ): boolean => {
    return (
      original.validation_enabled !== updated.validation_enabled ||
      original.primary_region !== updated.primary_region ||
      original.secondary_region !== updated.secondary_region
    );
  };

  const stopLoadingAndCloseModal = () => {
    setDialogOpen(false);
  };

  const killProcessAndReportError = (e: any) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const patchResourceData = async ({
    workload,
    resourceName,
    payload,
  }: {
    workload?: string;
    resourceName?: string;
    payload: { [_: string]: Object };
  }) => {
    return workloadClient.apiFetch(
      `workloads/workload/${workload}/resource/${resourceName}`,
      'PATCH',
      payload,
    );
  };

  const callPatchApi = async () => {
    const payload = {
      disaster_recovery: updatedDisasterRecovery,
    };
    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (catalogLocationStringList) {
      accountObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve(false);
    }

    if (isValueDefined(accountObject?.workload)) {
      try {
        const patchResourceDataResponse = await patchResourceData({
          workload: accountObject.workload,
          resourceName: accountObject.resourceName,
          payload,
        });

        if (!isOkayResponse(patchResourceDataResponse)) {
          stopLoadingAndCloseModal();
          alertApi.post({
            message: getMessage({
              failureToDo: 'update your resource with the data provided',
            }).FAILURE,
            severity: SeverityEnum.ERROR,
          });
        } else {
          alertApi.post({
            message: getMessage({
              successMessage:
                'Disaster recovery configuration updated successfully.',
            }).SUCCESS,
            severity: SeverityEnum.INFO,
          });

          // Call the callback to update the parent component's state
          onUpdate?.(updatedDisasterRecovery);
        }
      } catch (e: any) {
        killProcessAndReportError(e);
      }
    }

    // Only refresh entity if no callback is provided (fallback behavior)
    if (!onUpdate) {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
    }

    return Promise.resolve(true);
  };

  // Handlers for dialog actions
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  // Handler for saving changes
  async function handleConfirm(e: any) {
    e.preventDefault();
    setLoading(true);
    await callPatchApi();
    setLoading(false);
    handleCloseDialog();
  }

  return (
    <BaseEditIcon
      workloadData={workloadData}
      requesterEmail={profile?.email ?? ''}
      handleClickOpen={handleOpenDialog}
      isModalOpen={dialogOpen}
      handleClose={handleCloseDialog}
      handleConfirm={handleConfirm}
      disableConfirm={
        !hasDisasterRecoveryChanged(disasterRecovery, updatedDisasterRecovery)
      }
      isLoading={loading}
      isLoadingEdit={workloadLoading}
      dialogTitle="Update Disaster Recovery"
      allowEngineerEdit
      apiValidationPayload=""
    >
      <DisasterRecoveryEditDialog
        disasterRecovery={disasterRecovery}
        setDisasterRecovery={setUpdatedDisasterRecovery}
      />
    </BaseEditIcon>
  );
};
